"use client";

import { useContext } from "react";

import UploadFile, {
    type UploadFileOptions,
    type UploadFileSingleProps,
} from "@/components/utils/uploadFile";
import type { LomdaV3AssetType } from "@/lib/utils/lomdaV3/types";
import { PickGuardContext, usePickGuard } from "../../../hooks/usePickGuard";
import { useLomdaUploadTarget } from "../../../hooks/useLomdaUploadTarget";
import { MediaSourceTabs, type MediaSourceLayout } from "./mediaSourceTabs";

interface LomdaUploadFileProps extends Omit<UploadFileSingleProps, "options"> {
    options: Omit<UploadFileOptions, "fileUploadPath" | "uniqueFileName">;
    folder?: string;
    // Icons and thumbnails stay out of the media library.
    record?: boolean;
    // Adds a "Reuse media" tab listing the library's files of this kind.
    reuse?: LomdaV3AssetType;
    reuseLayout?: MediaSourceLayout;
    selectedUrl?: string;
}

/** Every lomda upload: the lomda's folder, a unique name, recorded in the library. */
export default function LomdaUploadFile({
    options,
    folder,
    record = true,
    reuse,
    reuseLayout,
    selectedUrl,
    onUploadFinished,
    ...props
}: LomdaUploadFileProps) {
    const target = useLomdaUploadTarget(folder);
    const ownGuard = usePickGuard();
    // Inside a MediaSourceTabs (a video's upload card, a thumbnail) its guard.
    const guard = useContext(PickGuardContext) ?? ownGuard;

    const upload = (
        <UploadFile
            {...props}
            options={{
                ...options,
                fileUploadPath: target.fileUploadPath,
                uniqueFileName: true,
            }}
            // Called with the handler from drop time, so the file lands in the
            // slot it was dropped on. The handler patches the latest store data.
            onUploadFinished={(file) => {
                if (!target.sameLomda()) return;
                if (!guard.pickedSince()) onUploadFinished(file);
                if (record) target.record(file, reuse);
            }}
        />
    );
    if (!reuse) return upload;

    return (
        <MediaSourceTabs
            kind={reuse}
            limits={options}
            guard={ownGuard}
            layout={reuseLayout}
            selectedUrl={selectedUrl}
            upload={upload}
            onPick={(item) =>
                onUploadFinished({
                    fileUrl: item.url,
                    fileName: item.name,
                    fileSize: item.size,
                })
            }
        />
    );
}
